import React, { useEffect, useMemo, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { HistoryEntry } from '../../../../types/proDocuments';

export interface DocsHistoryDrawerProps {
  open: boolean;
  title?: string;
  entries: HistoryEntry[];
  loading?: boolean;
  onClose: () => void;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatDateTime = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()} ${pad2(
    d.getHours(),
  )}:${pad2(d.getMinutes())}`;
};

const renderEntryText = (entry: HistoryEntry): string => {
  const time = formatDateTime(entry.datetime);
  const user = entry.userid;
  const t = (entry.type || '').toLowerCase();

  if (t === 'documentupload' && !entry.colname) {
    return `${time} ${user} uploaded`;
  }
  if (t === 'documentdelete') {
    return `${time} ${user} deleted`;
  }
  if (entry.colname) {
    const oldVal = entry.oldval ?? '';
    const newVal = entry.newval ?? '';
    return `${time} ${user} changed ${entry.colname} from "${oldVal}" to "${newVal}"`;
  }
  // Fallback for unknown entry types
  return `${time} ${user} ${entry.type ?? ''}`.trim();
};

export const DocsHistoryDrawer: React.FC<DocsHistoryDrawerProps> = ({
  open,
  title,
  entries,
  loading = false,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = 'docs-history-drawer-title';

  // Defensive sort by datetime DESC.
  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      const ta = new Date(a.datetime).getTime() || 0;
      const tb = new Date(b.datetime).getTime() || 0;
      return tb - ta;
    });
  }, [entries]);

  // Escape closes drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 z-[80] bg-gray-900/40 backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-panel shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-theme-border-primary bg-theme-bg-tertiary px-4 py-3 sm:px-6">
          <h2
            id={titleId}
            className="text-base font-semibold text-heading"
          >
            {title ? `History — ${title}` : 'History'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close history"
            className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:hover:text-gray-200"
          >
            <XMarkIcon className="size-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {loading ? (
            <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
              />
              <span>Loading history…</span>
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">No history yet.</p>
          ) : (
            <ol className="space-y-2">
              {sorted.map((entry, idx) => (
                <li
                  key={`${entry.datetime}-${entry.userid}-${entry.colname ?? ''}-${idx}`}
                  className="text-sm text-gray-800 dark:text-gray-200"
                >
                  {renderEntryText(entry)}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocsHistoryDrawer;
